// PRICERECON-001 — display-only reconciliation of the market-detail price paths.
// The detail view draws price from FOUR unreconciled sources: header (alpaca-iex-snapshot,
// can be stale), candles + tooltip (alpaca_stocks_v2_iex bars, `adjustment=raw` so a series can
// span a split), and C4 levels (/ta/levels, a different basis). This helper detects when they
// disagree beyond tolerance and lets the view fail CLOSED — show an honest STALE / UNRECONCILED
// state instead of a confident wrong number. It NEVER fabricates or averages a price, and it
// changes NO integrity / decision_band / gating logic — display reconciliation only.

export type ReconcileVerdict = 'RECONCILED' | 'STALE' | 'UNRECONCILED';

export interface ReconcileBar { o?: number | null; h?: number | null; l?: number | null; c?: number | null; }
export interface ReconcileLevels {
  vwap?: number | null;
  prior_day_close?: number | null;
  breakout_trigger?: number | null;
}
export interface ReconcileInput {
  headerPrice: number | null;
  headerStale: boolean;
  headerQuality: string | null;                 // 'live' | 'stale' | 'after_hours' | ...
  bars: ReconcileBar[] | null | undefined;
  levels: ReconcileLevels | null | undefined;
  opts?: { splitRatio?: number; headerDivergePct?: number; levelsScaleMax?: number };
}
export interface ReconcileDivergence { path: string; value: number | null; note: string; }
export interface ReconcileResult {
  verdict: ReconcileVerdict;
  canonicalPrice: number | null;
  canonicalSource: 'header_quote' | 'candle_last' | null;
  candleLast: number | null;
  candleMin: number | null;
  candleMax: number | null;
  splitContaminated: boolean;
  reasons: string[];
  divergences: ReconcileDivergence[];
}

// Defaults (PRICERECON-002): a single-session series should not span >5x unless it straddles a
// split / data error; a fresh header should sit within ~10% of the last candle; C4 levels are
// reference lines (VWAP / prior-day close) that are legitimately FAR from a runaway price, so we
// only flag them when they are on a different SCALE (>=5x off = a genuine raw-vs-adjusted basis
// mismatch), NOT merely distant in price.
const DEFAULTS = { splitRatio: 5.0, headerDivergePct: 0.10, levelsScaleMax: 5.0 };

function _num(v: unknown): number | null {
  return typeof v === 'number' && isFinite(v) ? v : null;
}

export function reconcileDetailPrice(input: ReconcileInput): ReconcileResult {
  const o = { ...DEFAULTS, ...(input.opts || {}) };
  const closes = (input.bars || [])
    .map((b) => _num(b?.c))
    .filter((c): c is number => c !== null && c > 0);
  const candleLast = closes.length ? closes[closes.length - 1] : null;
  const candleMin = closes.length ? Math.min(...closes) : null;
  const candleMax = closes.length ? Math.max(...closes) : null;

  const reasons: string[] = [];
  const divergences: ReconcileDivergence[] = [];

  // 1. Split contamination — one series spanning an implausible range (raw/unadjusted across a split).
  const splitContaminated =
    candleMin !== null && candleMax !== null && candleMin > 0 && candleMax / candleMin >= o.splitRatio;
  if (splitContaminated && candleMin && candleMax) {
    reasons.push(
      `Candle series spans ${(candleMax / candleMin).toFixed(1)}x ($${candleMin.toFixed(2)}–$${candleMax.toFixed(2)}) — possible split on a raw/unadjusted series (candles/tooltip may mix pre/post-split).`,
    );
    divergences.push({ path: 'candles+tooltip', value: candleLast, note: `range $${candleMin.toFixed(2)}–$${candleMax.toFixed(2)}` });
  }

  // 2. Header quote vs last candle — hard divergence.
  const hp = _num(input.headerPrice);
  let headerDiverged = false;
  if (hp !== null && candleLast !== null && candleLast > 0) {
    const diff = Math.abs(hp - candleLast) / candleLast;
    if (diff > o.headerDivergePct) {
      headerDiverged = true;
      reasons.push(`Header quote $${hp.toFixed(2)} diverges ${(diff * 100).toFixed(0)}% from last candle $${candleLast.toFixed(2)}.`);
    }
    divergences.push({ path: 'header', value: hp, note: input.headerStale ? 'stale snapshot' : 'quote' });
  } else if (hp !== null) {
    divergences.push({ path: 'header', value: hp, note: input.headerStale ? 'stale snapshot' : 'quote' });
  }

  // 3. C4 levels vs last candle — SCALE / basis mismatch only. Levels (VWAP / prior-day close)
  //    are reference lines that legitimately sit far from a fast-moving price, so we flag ONLY an
  //    order-of-magnitude gap (raw-vs-adjusted split basis), not normal level distance.
  const ref = _num(input.levels?.prior_day_close) ?? _num(input.levels?.vwap);
  let levelsDiverged = false;
  if (ref !== null && ref > 0 && candleLast !== null && candleLast > 0) {
    const ratio = ref / candleLast;
    if (ratio >= o.levelsScaleMax || ratio <= 1 / o.levelsScaleMax) {
      levelsDiverged = true;
      reasons.push(`C4 levels basis ~$${ref.toFixed(2)} is on a different SCALE (${ratio.toFixed(1)}x) from the last candle $${candleLast.toFixed(2)} — likely a raw-vs-split-adjusted basis mismatch.`);
    }
    divergences.push({ path: 'c4_levels', value: ref, note: 'levels basis' });
  }

  // Verdict — fail closed.
  let verdict: ReconcileVerdict;
  if (splitContaminated || headerDiverged || levelsDiverged) {
    verdict = 'UNRECONCILED';
  } else if (input.headerStale || input.headerQuality === 'stale') {
    verdict = 'STALE';
    reasons.push('Header quote is stale; it may not reflect the current price.');
  } else {
    verdict = 'RECONCILED';
  }

  // Canonical price — prefer a fresh trustworthy header; else the last candle when NOT contaminated;
  // never invent a number when unreconciled.
  let canonicalPrice: number | null = null;
  let canonicalSource: ReconcileResult['canonicalSource'] = null;
  const headerFresh = hp !== null && !input.headerStale && (input.headerQuality === 'live' || input.headerQuality === 'after_hours');
  if (verdict === 'RECONCILED' && headerFresh) {
    canonicalPrice = hp; canonicalSource = 'header_quote';
  } else if (verdict === 'RECONCILED' && candleLast !== null) {
    canonicalPrice = candleLast; canonicalSource = 'candle_last';
  } else if (verdict === 'STALE' && candleLast !== null && !splitContaminated) {
    canonicalPrice = candleLast; canonicalSource = 'candle_last';
  }

  return {
    verdict, canonicalPrice, canonicalSource, candleLast, candleMin, candleMax,
    splitContaminated, reasons, divergences,
  };
}
